import { Link } from "@tanstack/react-router";
import {
  Wind,
  Sprout,
  Flower,
  Hand,
  Music,
  Moon,
  MoonStar,
  NotebookPen,
  BookOpen,
  Sparkles,
  Users,
  UserCheck,
  CalendarDays,
  LifeBuoy,
  Library,
  Compass,
  ShieldCheck,
  Palette,
  Heart,
  Sun,
  Eye,
  Activity,
  Waves,
  Lock,
  ArrowRight,
  type LucideIcon,
} from "lucide-react";
import { useMembership } from "@/hooks/useMembership";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";

import anxiousArt from "@/assets/feelings/feel-anxious.webp.asset.json";
import disconnectedArt from "@/assets/feelings/feel-disconnected.webp.asset.json";
import sadArt from "@/assets/feelings/feel-sad.webp.asset.json";
import triggeredArt from "@/assets/feelings/feel-triggered.webp.asset.json";
import connectionArt from "@/assets/feelings/feel-connection.webp.asset.json";
import understandArt from "@/assets/feelings/feel-understand.webp.asset.json";

type NeedRoute =
  | "/breathing"
  | "/grounding"
  | "/tapping"
  | "/sounds"
  | "/somatic"
  | "/meditations"
  | "/sleep"
  | "/journal"
  | "/dreams"
  | "/dream-reading"
  | "/dream-reflections"
  | "/guided-journal"
  | "/shadow-work"
  | "/shadow-journey"
  | "/safety-stabilisation"
  | "/know-your-patterns"
  | "/creative-reflection"
  | "/inner-child"
  | "/community"
  | "/events"
  | "/therapist-directory"
  | "/support"
  | "/library"
  | "/check-in"
  | "/dissociation-journal"
  | "/abuse-journal"
  | "/affirmations"
  | "/music-journal"
  | "/bilateral"
  | "/sleep-dreams"
  | "/join-community"
  | "/learn/assessments";

type Tool = { label: string; to: NeedRoute; icon: LucideIcon };

type Need = {
  id: string;
  emoji: string;
  title: string;
  description: string;
  art: { url: string };
  artAlt: string;
  tools: Tool[];
  exploreTo?: NeedRoute;
  exploreLabel?: string;
};

const NEEDS: Need[] = [
  {
    id: "journal",
    emoji: "📖",
    title: "Journal My Thoughts",
    description: "Make space for what you're carrying — one page at a time.",
    art: understandArt,
    artAlt: "Soft cartoon of a person writing in a journal",
    tools: [
      { label: "Recovery Journal", to: "/journal", icon: BookOpen },
      { label: "Dream Journal", to: "/dreams", icon: Moon },
      { label: "Dissociation Journal", to: "/dissociation-journal", icon: Sparkles },
      { label: "Abuse Journal", to: "/abuse-journal", icon: Library },
      { label: "Guided Journal", to: "/guided-journal", icon: Compass },
      { label: "Music Journal", to: "/music-journal", icon: Music },
    ],
  },
  {
    id: "calm-anxiety",
    emoji: "🌬️",
    title: "Calm My Anxiety",
    description: "Soothe a racing mind and signal safety to your body.",
    art: anxiousArt,
    artAlt: "Soft cartoon of a person breathing slowly with calm clouds",
    tools: [
      { label: "Meditation", to: "/meditations", icon: Flower },
      { label: "4-7-8 Breathing", to: "/breathing", icon: Wind },
      { label: "EFT Tapping", to: "/tapping", icon: Hand },
      { label: "Calming Sounds", to: "/sounds", icon: Music },
      { label: "Grounding", to: "/grounding", icon: Sprout },
      { label: "Somatic Practice", to: "/somatic", icon: Waves },
    ],
  },
  {
    id: "feel-grounded",
    emoji: "🌿",
    title: "Feel More Grounded",
    description: "Come back to the present moment, gently and safely.",
    art: disconnectedArt,
    artAlt: "Soft cartoon of a person reconnecting with a leafy tree",
    tools: [
      { label: "5-4-3-2-1 Grounding", to: "/grounding", icon: Sprout },
      { label: "Safe Place", to: "/grounding", icon: Sprout },
      { label: "Feet on the Ground", to: "/grounding", icon: Sprout },
      { label: "Orienting Exercise", to: "/grounding", icon: Eye },
      { label: "Breathing", to: "/breathing", icon: Wind },
      { label: "Bilateral", to: "/bilateral", icon: Activity },
    ],
  },
  {
    id: "improve-sleep",
    emoji: "😴",
    title: "Improve Sleep",
    description: "Soft rituals to rest a tired nervous system.",
    art: sadArt,
    artAlt: "Soft cartoon of a person wrapped in a blanket resting peacefully",
    tools: [
      { label: "Sleep Meditations", to: "/sleep", icon: Moon },
      { label: "Bedtime Breathing", to: "/breathing", icon: Wind },
      { label: "Sleep & Rest Library", to: "/sleep", icon: Moon },
      { label: "Relaxation Sounds", to: "/sounds", icon: Music },
      { label: "Somatic Release", to: "/somatic", icon: Waves },
      { label: "Dream Journal", to: "/sleep-dreams", icon: BookOpen },
    ],
  },
  {
    id: "record-dreams",
    emoji: "🌙",
    title: "Record My Dreams",
    description: "Keep last night's dream, then gently explore what it stirred.",
    art: sadArt,
    artAlt: "Soft cartoon of a person wrapped in a blanket resting peacefully",
    tools: [
      { label: "Dream Journal", to: "/sleep-dreams", icon: MoonStar },
      { label: "Dream Reading", to: "/dream-reading", icon: Sparkles },
      { label: "Dream Reflections", to: "/dream-reflections", icon: NotebookPen },
      { label: "Sleep Support", to: "/sleep", icon: Moon },
      { label: "Calming Sounds", to: "/sounds", icon: Music },
      { label: "Bedtime Breathing", to: "/breathing", icon: Wind },
    ],
    exploreTo: "/sleep-dreams",
    exploreLabel: "Open your dream journal",
  },
  {
    id: "write-journal",
    emoji: "✍️",
    title: "Write My Journal",
    description: "Set down what today held, in your own words and time.",
    art: understandArt,
    artAlt: "Soft cartoon of a person writing reflectively by candlelight",
    tools: [
      { label: "Recovery Journal", to: "/journal", icon: BookOpen },
      { label: "Dream Journal", to: "/sleep-dreams", icon: MoonStar },
      { label: "Guided Journal", to: "/guided-journal", icon: Compass },
      { label: "Dissociation Journal", to: "/dissociation-journal", icon: Sparkles },
      { label: "Music Journal", to: "/music-journal", icon: Music },
      { label: "Daily Affirmations", to: "/affirmations", icon: Sun },
    ],
    exploreTo: "/journal",
    exploreLabel: "Open my journal",
  },
  {
    id: "release-stress",
    emoji: "⚡",
    title: "Release Stress",
    description: "Move stored tension out of the body, gently.",
    art: triggeredArt,
    artAlt: "Soft cartoon of a person releasing tension through gentle movement",
    tools: [
      { label: "Somatic Practices", to: "/somatic", icon: Flower },
      { label: "Shaking Exercise", to: "/somatic", icon: Waves },
      { label: "Progressive Relaxation", to: "/grounding", icon: Activity },
      { label: "EFT Tapping", to: "/tapping", icon: Hand },
      { label: "Breathing", to: "/breathing", icon: Wind },
      { label: "Grounding", to: "/grounding", icon: Sprout },
    ],
  },
  {
    id: "process-emotions",
    emoji: "💛",
    title: "Process My Emotions",
    description: "Honour what's surfacing with reflective, gentle tools.",
    art: understandArt,
    artAlt: "Soft cartoon of a person writing reflectively by candlelight",
    tools: [
      { label: "Guided Shadow Journal", to: "/shadow-journey", icon: Compass },
      { label: "Safety & Stabilisation", to: "/safety-stabilisation", icon: ShieldCheck },
      { label: "Know Your Patterns", to: "/know-your-patterns", icon: Eye },
      { label: "Creative Reflection", to: "/creative-reflection", icon: Palette },
      { label: "Inner Child Work", to: "/inner-child", icon: Heart },
      { label: "Recovery Journal", to: "/journal", icon: BookOpen },
    ],
  },
  {
    id: "connect",
    emoji: "🤝",
    title: "Connect With Others",
    description: "You don't have to heal alone. Find people and support.",
    art: connectionArt,
    artAlt: "Soft cartoon of people sitting close together with a heart above",
    tools: [
      { label: "Community", to: "/community", icon: Users },
      { label: "Live Events", to: "/events", icon: CalendarDays },
      { label: "Therapist Directory", to: "/therapist-directory", icon: UserCheck },
      { label: "Support Resources", to: "/support", icon: LifeBuoy },
      { label: "Join the Community", to: "/join-community", icon: Heart },
      { label: "Learning Hub", to: "/library", icon: Library },
    ],
  },
];

export function NeedsCheck() {
  const membership = useMembership();
  // Only lock once status is known, so members never see a flash of the lock.
  const locked = !membership.loading && !membership.isMember && !membership.isAdmin;
  return (
    <Carousel
      opts={{ align: "start", loop: true }}
      className="w-full"
      aria-label="What do you need right now"
    >
      <CarouselContent className="-ms-5">
        {NEEDS.map((n) => (
          <CarouselItem
            key={n.id}
            className="ps-5 basis-[88%] sm:basis-1/2 lg:basis-1/2"
          >
            <article
              className={[
                "group relative flex h-full flex-col rounded-[2rem] p-6 sm:p-8 transition-all duration-300",
                "hover:-translate-y-1",
                "bg-gradient-to-br from-[oklch(0.97_0.025_85)] via-card to-[oklch(0.93_0.05_82)]",
                "dark:from-[oklch(0.30_0.015_78)] dark:via-card dark:to-[oklch(0.34_0.04_80)]",
                "border border-gold-light/60 dark:border-gold-light/40",
                "shadow-[0_24px_60px_-32px_color-mix(in_oklab,var(--gold-deep)_45%,transparent)]",
              ].join(" ")}
            >
              <div className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--gold-deep)]">
                <span aria-hidden>{n.emoji}</span> What you need right now
              </div>

              <div className="mt-3 flex items-start gap-4">
                <div
                  aria-hidden
                  className="relative shrink-0 h-24 w-24 sm:h-28 sm:w-28 rounded-3xl bg-[radial-gradient(ellipse_at_center,_var(--cream)_0%,_color-mix(in_oklab,var(--gold-light)_55%,var(--cream))_75%,_transparent_100%)] ring-1 ring-gold-light/50 overflow-hidden"
                >
                  <img
                    src={n.art.url}
                    alt=""
                    loading="lazy"
                    width={224}
                    height={224}
                    className="absolute inset-0 h-full w-full object-contain p-1.5"
                  />
                </div>
                <div className="min-w-0 pt-1">
                  <h3 className="font-display text-xl leading-tight text-foreground sm:text-2xl">
                    {n.title}
                  </h3>
                </div>
              </div>

              <p className="mt-4 text-sm leading-relaxed text-foreground/80 sm:text-base">
                {n.description}
              </p>

              <div className="mt-auto pt-6">
                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--gold-deep)]">
                  Recommended
                </div>
                <ul className="mt-3 grid grid-cols-2 gap-2.5">
                  {n.tools.map((t) => {
                    const ToolIcon = t.icon;
                    return (
                      <li key={t.label}>
                        <Link
                          to={t.to}
                          aria-label={`Open ${t.label}`}
                          className={[
                            "flex h-full w-full items-center gap-2 rounded-xl border px-3 py-2.5 text-xs font-medium shadow-sm transition-all sm:text-sm",
                            "hover:-translate-y-0.5 hover:shadow-md hover:border-gold/60",
                            "bg-cream/80 border-gold-light/70 text-foreground",
                            "dark:bg-card/60 dark:border-gold-light/40",
                          ].join(" ")}
                        >
                          <ToolIcon className="h-4 w-4 shrink-0 text-[var(--gold-deep)]" strokeWidth={2} />
                          <span className="leading-tight">{t.label}</span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
                {locked ? (
                  <div className="mt-3 rounded-2xl border-2 border-gold/30 bg-card/90 px-4 py-4 text-center">
                    <p className="font-display text-base font-semibold text-foreground">
                      <Lock aria-hidden className="mr-1 inline h-4 w-4 text-[var(--gold-deep)]" />
                      Continue with Recovery Trauma™
                    </p>
                    <p className="mt-1 text-xs leading-relaxed text-foreground/80">
                      Explore the tools above — members unlock the full guided exercises, voice, video and journals.
                    </p>
                    <div className="mt-3 flex flex-col items-center gap-2">
                      <Link
                        to="/membership"
                        className="inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-full bg-[var(--gold-deep)] px-5 text-sm font-semibold text-primary-foreground"
                      >
                        Join Recovery Trauma™ <ArrowRight aria-hidden className="h-4 w-4" />
                      </Link>
                      <Link to="/login" className="text-xs font-semibold text-[var(--gold-deep)] underline-offset-4 hover:underline">
                        Already a member? Log in
                      </Link>
                    </div>
                  </div>
                ) : n.exploreTo ? (
                  <Link
                    to={n.exploreTo}
                    className="mt-3 inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.14em] text-[var(--gold-deep)] underline-offset-4 hover:underline sm:text-sm"
                  >
                    {n.exploreLabel ?? "Explore all"} →
                  </Link>
                ) : null}
              </div>

              <span className="sr-only">{n.artAlt}</span>
            </article>
          </CarouselItem>
        ))}
      </CarouselContent>
      <CarouselPrevious className="flex h-8 w-8 -start-3 border-gold/40 bg-cream/80 text-[var(--gold-deep)] hover:bg-cream sm:h-9 sm:w-9" />
      <CarouselNext className="flex h-8 w-8 -end-3 border-gold/40 bg-cream/80 text-[var(--gold-deep)] hover:bg-cream sm:h-9 sm:w-9" />
    </Carousel>
  );
}
